
import { v4 as uuidv4 } from "uuid";
import { 
  PlayerObject, 
  SmartHash,
  UpsertableBase
} from "@models/UpsertableBase";
import { 
  AdventureSpread,
  Character, 
  COptions,
  CharacterSubscriptionHolder,
  Encounter,
  ItemLimitation,
  Recipe,
  TrueCreatureInstance,
  CharacterItem,
  Money,
  CharacterArmor,
  CharacterWeapon,
  CharacterAmmunition,
  CharacterContainer,
  CharacterDeck,
  CharacterGem,
  CharacterBook,
  CharacterCustomItem,
  CharacterBasicItem, 
} from "@dndModels";
import { ArchivableCampaignObjectType, DataType } from "@dndModels/Types";
import { User } from "@models";

import CommonUtilities from "@utilities/common_utilities";

const common_util = CommonUtilities.getInstance();

export class Campaign extends PlayerObject {
  data_type: DataType = "campaign";
  subscription_holder = new CharacterSubscriptionHolder();
  archived_data_holder: SmartHash = {};
  last_changed = 0;
  last_saved = 0;
  party_name = "";
  current_date = 0; // This is a numeric representation of the current date in game.
  calendar_id = "";

  changed() {
    this.last_changed = common_util.get_time_number();
    this.subscription_holder.campaignChanged();
  }

  old_player_ids: string[] = [];
  c_players?: CampaignPlayers;
  get player_ids() {
    if (this.c_players) {
      return this.c_players.player_ids;
    }
    return this.old_player_ids;
  }
  get players() {
    if (this.c_players) {
      return this.c_players.the_players;
    }
    return [];
  }

  inventory: CampaignInventory = new CampaignInventory();

  old_reusable_invite = "";
  get reusable_invite() {
    if (this.c_players) {
      return this.c_players.reusable_invite;
    }
    return this.old_reusable_invite;
  }
  get invites() {
    if (this.c_players) {
      return this.c_players.invites;
    }
    return [];
  }
  set invites(value: string[]) {
    if (this.c_players) {
      this.c_players.invites = value;
    }
  }

  old_c_options = new COptions();
  char_options?: CampaignCharOptions;
  get c_options() {
    if (this.char_options) {
      return this.char_options.char_options;
    }
    return this.old_c_options;
  }

  c_chars: CampaignChar[] = [];
  get characters() {
    return this.c_chars.map(o => o.char).filter(o => o !== undefined) as Character[];
  }
  get active_characters() {
    return this.c_chars.filter(o => o.active).map(o => o.char).filter(o => o !== undefined) as Character[];
  }
  // org_inst_ids: string[] = [];
  // veh_inst_ids: string[] = [];
  // decks_of_many_things: { id: string, visible: boolean }[] = [];
  c_decks: CampaignDeck[] = [];
  is_favorite = false;
  description = "";
  last_updated = 0;

  campaign_recipes_loaded = false;
  campaign_recipes: CampaignRecipe[] = [];

  notes: CampaignNote[] = [];
  npcs: CampaignNPC[] = [];
  encounters: Encounter[] = [];
  quest_boards: QuestBoard[] = [];
  letters: Letter[] = [];

  // Archived objects aren't loaded with the rest, only when View Archives is checked.
  archived_notes: CampaignNote[] = [];
  archived_npcs: CampaignNPC[] = [];
  archived_encounters: Encounter[] = [];
  archived_quest_boards: QuestBoard[] = [];
  archived_letters: Letter[] = [];
  archives_loaded: ArchivableCampaignObjectType[] = [];

  get_campaign_objects(data_type: ArchivableCampaignObjectType, archived = false): CampaignObject[] {
    switch (data_type) {
      case "campaign_note": return archived ? this.archived_notes : this.notes;
      case "campaign_npc": return archived ? this.archived_npcs : this.npcs;
      case "encounter": return archived ? this.archived_encounters : this.encounters;
      case "campaign_quest_board": return archived ? this.archived_quest_boards : this.quest_boards;
      case "campaign_letter": return archived ? this.archived_letters : this.letters;
    }
  }

  set_campaign_objects(data_type: ArchivableCampaignObjectType, objects: CampaignObject[], archived = false) {
    switch (data_type) {
      case "campaign_note":
        if (archived) { this.archived_notes = objects as CampaignNote[]; } else { this.notes = objects as CampaignNote[]; }
      break;
      case "campaign_npc":
        if (archived) { this.archived_npcs = objects as CampaignNPC[]; } else { this.npcs = objects as CampaignNPC[]; }
      break;
      case "encounter":
        if (archived) { this.archived_encounters = objects as Encounter[]; } else { this.encounters = objects as Encounter[]; }
      break;
      case "campaign_quest_board":
        if (archived) { this.archived_quest_boards = objects as QuestBoard[]; } else { this.quest_boards = objects as QuestBoard[]; }
      break;
      case "campaign_letter":
        if (archived) { this.archived_letters = objects as Letter[]; } else { this.letters = objects as Letter[]; }
      break;
    }
  }

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.old_player_ids = obj.player_ids ? obj.player_ids : [];
      this.old_reusable_invite = obj.reusable_invite ? obj.reusable_invite : "";
      if (obj.c_options) {
        this.old_c_options = new COptions(obj.c_options);
      } 
      this.is_favorite = obj.is_favorite ? obj.is_favorite : false;
      // this.org_inst_ids = obj.org_inst_ids ? obj.org_inst_ids : [];
      // this.veh_inst_ids = obj.veh_inst_ids ? obj.veh_inst_ids : [];
      // this.decks_of_many_things = obj.decks_of_many_things ? obj.decks_of_many_things : [];
      this.description = obj.description ? obj.description : "";
      this.last_updated = obj.last_updated ? obj.last_updated : 0;
      this.party_name = obj.party_name ? obj.party_name : "";
      this.current_date = obj.current_date ? obj.current_date : 0;
      this.calendar_id = obj.calendar_id ? obj.calendar_id : "";
    }
    this.old_player_ids = this.player_ids.filter(o => o !== "");
  }

  get true_name() {
    return this.name;
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      c_options: this.c_options.toDBObj(),
      is_favorite: this.is_favorite,
      // org_inst_ids: this.org_inst_ids,
      // veh_inst_ids: this.veh_inst_ids,
      // decks_of_many_things: this.decks_of_many_things,
      description: this.description,
      last_updated: this.last_updated,
      party_name: this.party_name,
      current_date: this.current_date,
      calendar_id: this.calendar_id,
    };
  }

  copy(copyMe: Campaign, drop_ids = false): void {
    super.copy(copyMe, drop_ids);

    this.old_player_ids = copyMe.old_player_ids;
    this.c_players = copyMe.c_players ? copyMe.c_players.clone() : undefined;

    this.inventory = copyMe.inventory.clone();
    
    this.old_c_options = copyMe.old_c_options.clone();
    this.char_options = copyMe.char_options;
    
    // this.org_inst_ids = [...copyMe.org_inst_ids];
    // this.veh_inst_ids = [...copyMe.veh_inst_ids];
    // this.decks_of_many_things = copyMe.decks_of_many_things.map(o => { return {...o}; });
    this.c_decks = [...copyMe.c_decks];
    this.c_chars = [...copyMe.c_chars];
    this.description = copyMe.description;
    this.last_updated = copyMe.last_updated;
    this.notes = [...copyMe.notes];
    this.npcs = [...copyMe.npcs];
    this.quest_boards = [...copyMe.quest_boards];
    this.encounters = [...copyMe.encounters];
    this.party_name = copyMe.party_name;
    this.current_date = copyMe.current_date;
    this.calendar_id = copyMe.calendar_id;
  }

  clone(drop_ids = false): Campaign {
    const new_obj = new Campaign();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  baseCopy(copyMe: Campaign, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
  }

  baseClone(drop_ids = false): Campaign {
    const new_obj = new Campaign();
    new_obj.baseCopy(this, drop_ids);
    return new_obj;
  }
}
export class CampaignObject extends UpsertableBase {
  data_type: DataType = "campaign_object";
  campaign_id = "";
  is_archived = false;
  campaign?: Campaign;

  get my_notes() {
    if (this.data_type === "campaign_note") {
      return [];
    }
    if (this.campaign) {
      return this.campaign.notes.filter(o => o.on_type === this.data_type && o.on_id === this._id);
    }
    return [];
  }

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.campaign_id = obj.campaign_id ? obj.campaign_id : "";
      this.is_archived = obj.is_archived ? obj.is_archived : false;
    }
  }

  toDBObj(): any {
    if (this.data_type === "model_base" ||
      this.data_type === "player_object" ||
      this.data_type === "campaign_object" ||
      this.data_type === "upsertable_base"
    ) {
      console.error(`${this.data_type} is not meant to be saved to the db.`);
      debugger;
    }
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      campaign_id: this.campaign_id,
      is_archived: this.is_archived,
    };
  }

  copy(copyMe: CampaignObject, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.campaign_id = copyMe.campaign_id;
    this.is_archived = copyMe.is_archived;
  }

  clone(drop_ids = false): CampaignObject {
    const new_obj = new CampaignObject();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class CampaignPlayers extends CampaignObject {
  data_type: DataType = "campaign_players";
  players: CampaignPlayer[] = [];
  invites: string[] = [];
  reusable_invite = "";

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.invites = obj.invites ? obj.invites : [];
      this.reusable_invite = obj.reusable_invite ? obj.reusable_invite : uuidv4().toString();
    }
  }

  get the_players() {
    return this.players.filter(o => o.player !== undefined).map(o => o.player) as User[];
  }

  get player_ids() {
    return this.players.map(o => o.player_id);
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      invites: this.invites,
      reusable_invite: this.reusable_invite,
    };
  }

  generate_invite(): string {
    const invite = uuidv4().toString();
    this.invites.push(invite);
    return invite;
  }

  copy(copyMe: CampaignPlayers, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.players = [...copyMe.players];
    this.invites = [...copyMe.invites];
    this.reusable_invite = copyMe.reusable_invite;
  }

  clone(drop_ids = false): CampaignPlayers {
    const new_obj = new CampaignPlayers();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class CampaignPlayer extends CampaignObject {
  data_type: DataType = "campaign_player";
  player_id = "";
  player?: User;
  campaign_is_favorite = false;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.player_id = obj.player_id ? obj.player_id : "";
      this.campaign_is_favorite = obj.campaign_is_favorite ? obj.campaign_is_favorite : false;
    }
  }

  get true_name() {
    return this.name;
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      player_id: this.player_id,
      campaign_is_favorite: this.campaign_is_favorite,
    };
  }

  copy(copyMe: CampaignPlayer, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.player_id = copyMe.player_id;
    this.campaign_is_favorite = copyMe.campaign_is_favorite;
  }

  clone(drop_ids = false): CampaignPlayer {
    const new_obj = new CampaignPlayer();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class CampaignRecipe extends CampaignObject {
  data_type: DataType = "campaign_recipe";
  for_type: "Item" | "Magic Item Keyword" = "Item";
  for_id = "";
  recipes: Recipe[] = [];

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.for_type = obj.for_type ? obj.for_type : "Item";
      this.for_id = obj.for_id ? obj.for_id : "";
      this.recipes = obj.recipes ? obj.recipes.map((o: any) => new Recipe(o)) : [];
    }
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      for_type: this.for_type,
      for_id: this.for_id,
      recipes: this.recipes.map(o => o.toDBObj())
    };
  }

  copy(copyMe: CampaignRecipe, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.for_type = copyMe.for_type;
    this.for_id = copyMe.for_id;
    this.recipes = copyMe.recipes.map(o => o.clone(drop_ids));
  }

  clone(drop_ids = false): CampaignRecipe {
    const new_obj = new CampaignRecipe();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class CampaignChar extends CampaignObject {
  data_type: DataType = "campaign_char";
  char_id = "";
  active = true;
  char?: Character;
  hide_details = false;
  hide_char = false;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.char_id = obj.char_id ? obj.char_id : "";
      this.active = obj.active !== undefined ? obj.active : true;
      this.hide_details = obj.hide_details ? obj.hide_details : false;
      this.hide_char = obj.hide_char ? obj.hide_char : false;
    }
  }

  get true_name() {
    return this.name;
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      char_id: this.char_id,
      active: this.active,
      hide_details: this.hide_details,
      hide_char: this.hide_char,
    };
  }

  copy(copyMe: CampaignChar, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.char_id = copyMe.char_id;
    this.active = copyMe.active;
    this.hide_details = copyMe.hide_details;
    this.hide_char = copyMe.hide_char;
  }

  clone(drop_ids = false): CampaignChar {
    const new_obj = new CampaignChar();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class CampaignCharOptions extends CampaignObject {
  data_type: DataType = "campaign_char_options";
  char_options: COptions = new COptions();

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.char_options = obj.char_options ? new COptions(obj.char_options) : new COptions();;
    }
  }

  get true_name() {
    return this.name;
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      char_options: this.char_options.toDBObj(),
    };
  }

  copy(copyMe: CampaignCharOptions, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.char_options = copyMe.char_options.clone();
  }

  clone(drop_ids = false): CampaignCharOptions {
    const new_obj = new CampaignCharOptions();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class CampaignDeck extends CampaignObject {
  data_type: DataType = "campaign_deck";
  deck_id = "";
  card_ids: string[] = [];
  drawn_card_ids: string[] = [];
  saved_spreads: AdventureSpread[] = [];
  fudged_draw_ids: string[] = []; // This allows the DM select a particular card or set of cards to be drawn rather than the standard list.  Yes, it's cheating, but if done to make things more fun for the players then I'm okay with it.
  fudged_for_beneficial = false;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.deck_id = obj.deck_id ? obj.deck_id : "";
      this.card_ids = obj.card_ids ? obj.card_ids : [];
      this.drawn_card_ids = obj.drawn_card_ids ? obj.drawn_card_ids : [];
      this.saved_spreads = obj.saved_spreads ? obj.saved_spreads : [];
      this.fudged_draw_ids = obj.fudged_draw_ids ? obj.fudged_draw_ids : [];
      this.fudged_for_beneficial = obj.fudged_for_beneficial ? obj.fudged_for_beneficial : false;
    }
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      deck_id: this.deck_id,
      card_ids: this.card_ids,
      drawn_card_ids: this.drawn_card_ids,
      saved_spreads: this.saved_spreads,
      fudged_draw_ids: this.fudged_draw_ids,
      fudged_for_beneficial: this.fudged_for_beneficial,
    };
  }

  copy(copyMe: CampaignDeck, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.deck_id = copyMe.deck_id;
    this.card_ids = [...copyMe.card_ids];
    this.drawn_card_ids = [...copyMe.drawn_card_ids];
    this.saved_spreads = copyMe.saved_spreads.map(o => { 
      return { id: o.id, spread: o.spread };
    });
    this.fudged_draw_ids = [...copyMe.fudged_draw_ids];
    this.fudged_for_beneficial = copyMe.fudged_for_beneficial;
  }

  clone(drop_ids = false): CampaignDeck {
    const new_obj = new CampaignDeck();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class CampaignNote extends CampaignObject {
  data_type: DataType = "campaign_note";
  notes = "";
  position: { x: number, y: number } = { x: 0, y: 0 };
  on_type: DataType = "campaign";
  on_id = "";

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.notes = obj.notes ? obj.notes : "";
      this.position = obj.position ? obj.position : { x: 0, y: 0 };
      this.on_type = obj.on_type ? obj.on_type : "campaign";
      this.on_id = obj.on_id ? obj.on_id : "";
    }
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      notes: this.notes,
      position: this.position,
      on_type: this.on_type,
      on_id: this.on_id,
    };
  }

  copy(copyMe: CampaignNote, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.notes = copyMe.notes;
    this.position = {...copyMe.position};
    this.on_type = copyMe.on_type;
    this.on_id = copyMe.on_id;
  }

  clone(drop_ids = false): CampaignNote {
    const new_obj = new CampaignNote();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class CampaignNPC extends CampaignObject {
  data_type: DataType = "campaign_npc";
  notes = "";
  position: { x: number, y: number } = { x: 0, y: 0 };
  current_location = "";
  ci = new TrueCreatureInstance();
  char_id = "";
  char_sheet?: Character;
  inventory: VendorInvItem[] = []; 
  // This holds all the items a vendor has to sell. 
  // They are a set of Item Limits with associated counts (can be numbers or strings to account for whatever they might need to be) 
  // and prices (also can be numbers or strings).
  services: VendorService[] = []; 
  // Similar to inventory, but instead its services like Training, Healing, Cleansing, Resurrection, Transportation, Sending, etc.
  // Because of the nature of this, these are really more like additional notes, but with prices attached.
  will_buy = "";
  appearance = "";
  voice = "";

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.notes = obj.notes ? obj.notes : "";
      this.position = obj.position ? obj.position : { x: 0, y: 0 };
      this.current_location = obj.current_location ? obj.current_location : "";
      this.ci = obj.ci ? new TrueCreatureInstance(obj.ci) : new TrueCreatureInstance();
      this.char_id = obj.char_id ? obj.char_id : "";
      this.inventory = obj.inventory ? obj.inventory.map((o: any) => new VendorInvItem(o)) : [];
      this.services = obj.services ? obj.services.map((o: any) => new VendorService(o)) : [];
      this.will_buy = obj.will_buy ? obj.will_buy : "";
      this.appearance = obj.appearance ? obj.appearance : "";
      this.voice = obj.voice ? obj.voice : "";
    }
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      notes: this.notes,
      position: this.position,
      current_location: this.current_location,
      ci: this.ci.toDBObj(),
      char_id: this.char_id,
      inventory: this.inventory.map(o => o.toDBObj()),
      services: this.services.map(o => o.toDBObj()),
      will_buy: this.will_buy,
      appearance: this.appearance,
      voice: this.voice,
    };
  }

  copy(copyMe: CampaignNPC, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.name = copyMe.name;
    this.notes = copyMe.notes;
    this.position = {...copyMe.position};
    this.current_location = copyMe.current_location;
    this.ci = copyMe.ci.clone();
    this.char_id = copyMe.char_id;
    this.char_sheet = copyMe.char_sheet;
    this.inventory = copyMe.inventory.map(o => o.clone());
    this.services = copyMe.services.map(o => o.clone());
    this.will_buy = copyMe.will_buy;
    this.appearance = copyMe.appearance;
    this.voice = copyMe.voice;
  }

  clone(drop_ids = false): CampaignNPC {
    const new_obj = new CampaignNPC();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class VendorInvItem {
  true_id = "";
  name = "";
  item_limit = new ItemLimitation();
  amount = "";
  price = "";

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.name = obj.name ? obj.name : "";
      this.item_limit = obj.item_limit ? new ItemLimitation(obj.item_limit) : new ItemLimitation();
      this.amount = obj.amount ? obj.amount : "";
      this.price = obj.price ? obj.price : "";
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      name: this.name,
      item_limit: this.item_limit.toDBObj(),
      amount: this.amount,
      price: this.price,
    };
  }

  copy(copyMe: VendorInvItem, drop_ids = false): void {
    this.true_id = copyMe.true_id;
    this.name = copyMe.name;
    this.item_limit = copyMe.item_limit.clone();
    this.amount = copyMe.amount;
    this.price = copyMe.price;
  }

  clone(drop_ids = false): VendorInvItem {
    const new_obj = new VendorInvItem();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class VendorService {
  true_id = "";
  name = "";
  description = "";
  price = "";

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.name = obj.name ? obj.name : "";
      this.description = obj.description ? obj.description : "";
      this.price = obj.price ? obj.price : "";
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      name: this.name,
      description: this.description,
      price: this.price,
    };
  }

  copy(copyMe: VendorService, drop_ids = false): void {
    this.true_id = copyMe.true_id;
    this.name = copyMe.name;
    this.description = copyMe.description;
    this.price = copyMe.price;
  }

  clone(drop_ids = false): VendorService {
    const new_obj = new VendorService();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class QuestBoard extends CampaignObject {
  data_type: DataType = "campaign_quest_board";
  header = "Notice of the Town Magistrate";
  subheader = "Quests Posted This Day · All Adventurers Welcome";
  board_note = `Hear ye! The township has need of capable adventurers. Complete the deeds below, present yourself to the Magistrate for your seal of completion, and claim your earned reward. Choose your quests wisely.`;
  // I'll add additional options later for a seal to go at the top, other backgrounds, fonts, etc.
  quests: Quest[] = [];
  hidden = false; // Flag for whether to show it for players.

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.header = obj.header ? obj.header : "";
      this.subheader = obj.subheader ? obj.subheader : "";
      this.board_note = obj.board_note ? obj.board_note : "";
      this.quests = obj.quests ? obj.quests.map((o : any) => new Quest(o)) : [];
      this.hidden = obj.hidden ? obj.hidden : false;
    }
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      header: this.header,
      subheader: this.subheader,
      board_note: this.board_note,
      quests: this.quests.map(o => o.toDBObj()),
      hidden: this.hidden
    };
  }

  copy(copyMe: QuestBoard, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.name = copyMe.name;
    this.header = copyMe.header;
    this.subheader = copyMe.subheader;
    this.board_note = copyMe.board_note;
    this.quests = copyMe.quests.map(o => o.clone());
    this.hidden = copyMe.hidden;
  }

  clone(drop_ids = false): QuestBoard {
    const new_obj = new QuestBoard();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
export class Quest {
  true_id = "";
  title = "";
  body = "";
  reward = new QuestRewards();
  rewards_granted = false; // Set once the rewards have been given out, so they don't get given twice.
  completed = false;
  hidden = false; // Flag for whether to show it for players.

  // This is an estimate of how difficult a quest is expected to be based on the intel the quest givers have.
  // This should roughly equate to difficulty of encounters. 
  // The GM should adjust any encounters involved to fit the levels and number of the characters involved,
  // but also should remember that the intel of the quest giver isn't necessarily correct. An easy quest could actually be deadly.
  difficulty: "Easy" | "Medium" | "Hard" | "Deadly" = "Easy";
  // This is an estimate of how politically complicated the quest is expected to be based on the intel the quest givers have. 
  // 1 is simple, probably no politics involved. 
  // 2 probably has some minor politics involved, so only those who have shown that they can handle delicate 
  // political situations can take it.
  // 3 is rare, and usually means that someone high up politically is involved. 
  // These are usually assigned directly to teams that the heads of the organization trust 
  // to be able to handle that particular situation.
  stars: 1 | 2 | 3 = 1; 

  posted_by = ""; // This is who posted the quest. It may be an individual, a company, a guild, a gov't agency, etc.
  // This is a numeric representation of the in game "date" that a quest was posted to a quest board.
  // Quests that go too long without being taken by the party can be taken by other parties, 
  // or they may be cancelled or otherwise removed because they're no longer necessary 
  // (like because the clients died because it wasn't done).
  // I'm going to add a way of tracking the current date on Campaign later.
  posted = 0;
  
  // Quests can be claimed by parties. 
  // Other parties can't see quests you've claimed, but if you fail or give up, depending on the quest, 
  // it may get added back to the board, or maybe a new one can be added to clean up your mess.
  // A special option for it will be "The Party". The code will look for that, and treat it appropriately.
  claimed_by = ""; 

  get show() {
    return !this.hidden && (this.claimed_by === "" || this.claimed_by === "The Party");
  }

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.title = obj.title ? obj.title : "";
      this.body = obj.body ? obj.body : "";
      this.reward = new QuestRewards(obj.reward);
      this.rewards_granted = obj.rewards_granted ? obj.rewards_granted : false;
      this.completed = obj.completed ? obj.completed : false;
      this.hidden = obj.hidden ? obj.hidden : false;
      this.difficulty = obj.difficulty ? obj.difficulty : "Easy";
      this.stars = obj.stars ? obj.stars : 1;
      this.posted_by = obj.posted_by ? obj.posted_by : "";
      this.posted = obj.posted ? obj.posted : 0;
      this.claimed_by = obj.claimed_by ? obj.claimed_by : "";
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      title: this.title,
      body: this.body,
      reward: this.reward.toDBObj(),
      rewards_granted: this.rewards_granted,
      completed: this.completed,
      hidden: this.hidden,
      difficulty: this.difficulty,
      stars: this.stars,
      posted_by: this.posted_by,
      posted: this.posted,
      claimed_by: this.claimed_by,
    };
  }

  copy(copyMe: Quest): void {
    this.true_id = copyMe.true_id;
    this.title = copyMe.title;
    this.body = copyMe.body;
    this.reward = copyMe.reward.clone();
    this.rewards_granted = copyMe.rewards_granted;
    this.completed = copyMe.completed;
    this.hidden = copyMe.hidden;
    this.difficulty = copyMe.difficulty;
    this.stars = copyMe.stars;
    this.posted_by = copyMe.posted_by;
    this.posted = copyMe.posted;
    this.claimed_by = copyMe.claimed_by;
  }

  clone(): Quest {
    const new_obj = new Quest();
    new_obj.copy(this);
    return new_obj;
  }
}
export class QuestRewards {
  money = new Money();
  items: { 
    id: string, // id of the item
    count: number, // how many. Default to 1.
    for_all: boolean // If this is true then every party member gets one.
  }[] = [];
  text = ""; 

  constructor(obj?: any) {
    if (obj) {
      const reward = obj ? obj : "";
      if (typeof(reward) === "string") {
        this.text = reward;
        if (reward.includes("gp")) {
          const pieces = reward.split("gp");
          const gp = +pieces[0].trim();
          this.money.gold = gp;
        }
      } else if (typeof(reward) === "object") {
        this.money = reward.money ? new Money(reward.money) : new Money();
        this.items = reward.items ? reward.items : [];
        this.text = reward.text ? reward.text : "";
      } else {
        this.money = new Money(); 
        this.items = [];
        this.text = "";
      }
    }
  }

  toDBObj(): any {
    return {
      money: this.money.toDBObj(),
      items: this.items,
      text: this.text
    };
  }

  copy(copyMe: QuestRewards): void {
    this.money = copyMe.money.clone();
    this.items = copyMe.items.map(o => { return {...o}; });
    this.text = copyMe.text;
  }

  clone(): QuestRewards {
    const new_obj = new QuestRewards();
    new_obj.copy(this);
    return new_obj;
  }
}
export class Letter extends CampaignObject {
  data_type: DataType = "campaign_letter";
  body = "";
  hidden = false; // Flag for whether to show it for players.

  from = ""; 
  sent = 0;
  received = 0;
  
  to = "";

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.body = obj.body ? obj.body : "";
      this.hidden = obj.hidden ? obj.hidden : false;
      this.from = obj.from ? obj.from : "";
      this.sent = obj.sent ? obj.sent : 0;
      this.received = obj.received ? obj.received : 0;
      this.to = obj.to ? obj.to : "";
    }
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      body: this.body,
      hidden: this.hidden,
      from: this.from,
      sent: this.sent,
      received: this.received,
      to: this.to
    };
  }

  copy(copyMe: Letter, drop_ids: boolean = false): void {
    super.copy(copyMe, drop_ids);
    this.body = copyMe.body;
    this.hidden = copyMe.hidden;
    this.from = copyMe.from;
    this.sent = copyMe.sent;
    this.received = copyMe.received;
    this.to = copyMe.to;
  }

  clone(): Letter {
    const new_obj = new Letter();
    new_obj.copy(this);
    return new_obj;
  }
}

/**
 * Campaign Inventory is simpler than Character Inventory in a few ways. 
 * 
 * Items can't be equipped, attuned, or used while in Campaign Inventory, 
 * but I'm going to have tools for easily moving items or money
 * from Campaign Inventory to Character Inventory, and vice versa.
 * 
 * Future idea: Make containers in inventory able to store other items, 
 * and moving a container to a character will also move the items in it.
 * 
 */
export class CampaignInventory extends CampaignObject {
  data_type: DataType = "campaign_inventory";
  items: CharacterItem[] = [];
  money: Money = new Money();
  other_possessions = "";
  get containers() {
    return this.items.filter(o => o instanceof CharacterContainer) as CharacterContainer[];
  }
  get ammunitions() {
    return this.items.filter(o => o instanceof CharacterAmmunition) as CharacterAmmunition[];
  }

  get storage_container(): CharacterContainer {
    const storage_finder = this.containers.filter(o => o.true_id === "Campaign Storage");
    if (storage_finder.length === 1) {
      return storage_finder[0];
    } else {
      const storage = new CharacterContainer();
      storage.name = "Campaign Storage";
      storage.true_id = "Campaign Storage";
      this.containers.push(storage);
      this.items.push(storage);
      return storage;
    }
  }

  get weight_carried(): number {
    let weight = 0;

    return weight;
  }
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.items = [];
      if (obj.items && obj.items.length > 0) {
        const types: string[] = [];
        for (let i = 0; i < obj.items.length; i++) {
          const item = obj.items[i];
          if (!types.includes(item.item_type)) {
            types.push(item.item_type);
          }
          let char_item: CharacterItem | null = null;
          switch (item.item_type) {
            case "Armor":
              char_item = new CharacterArmor(item);
            break;
            case "Weapon":
              char_item = new CharacterWeapon(item);
            break;
            case "Ammunition":
              char_item = new CharacterAmmunition(item);
            break;
            case "Bag":
              char_item = new CharacterContainer(item);
              this.containers.push(char_item as CharacterContainer);
            break;
            case "Container":
              char_item = new CharacterContainer(item);
              this.containers.push(char_item as CharacterContainer);
            break;
            case "Deck":
              char_item = new CharacterDeck(item);
            break;
            case "Gem":
              char_item = new CharacterGem(item);
            break;
            case "Book":
              char_item = new CharacterBook(item);
            break;
            case "Custom":
              if (item.true_id === "Campaign Storage") {
                if (this.containers.filter(o => o.true_id === "Campaign Storage").length === 0) {
                  char_item = new CharacterContainer(item);
                  this.containers.push(char_item as CharacterContainer);
                }
              } else {
                char_item = new CharacterCustomItem(item);
              }
            break;
            default:
              char_item = new CharacterBasicItem(item);
            break;
          }
          if (char_item) {
            this.items.push(char_item);
          }
        }
        if (this.containers.filter(o => o.name === "Campaign Storage").length === 0) {
          const storage = new CharacterContainer();
          storage.name = "Campaign Storage";
          storage.true_id = "Campaign Storage";
          this.containers.push(storage);
          this.items.push(storage);
        }
      }
      const container_ids = this.items.filter(o => o.item_type === "Container").map(o => o.true_id);
      this.items = this.items.filter(o => 
        container_ids.includes(o.true_id) || 
        container_ids.includes(o.in_char_container_id));

      this.money = obj.money ? new Money(obj.money) : new Money();
      this.other_possessions = obj.other_possessions ? obj.other_possessions : "";
    }
  }

  toDBObj() {
    return {
      ...super.toDBObj(),
      items: this.items.map(o => o.toDBObj()),
      money: this.money.toDBObj(),
      other_possessions: this.other_possessions,
    };
  }

  clone(drop_ids = false): CampaignInventory {
    const clone = new CampaignInventory();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CampaignInventory, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.items = copyMe.items.map(o => o.clone());
    this.money = copyMe.money.clone();
    this.other_possessions = copyMe.other_possessions;
  }

  merge(copyMe: CampaignInventory, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    let ids = this.items.map(o => o.true_id);
    const new_items = copyMe.items.filter(o => !ids.includes(o.true_id));
    ids = copyMe.items.map(o => o.true_id);
    const removed_ids = this.items.filter(o => !ids.includes(o.true_id)).map(o => o.true_id);
    this.items.push(...new_items);
    this.items = this.items.filter(o => !removed_ids.includes(o.true_id));
    this.money.copy(copyMe.money);
    this.other_possessions = copyMe.other_possessions;
  }
}